import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Controller, type SubmitErrorHandler, useForm, useWatch } from 'react-hook-form';
import { useRouteMatch } from 'react-router';
import { toast } from 'sonner';
import * as yup from 'yup';
import { FormFieldType, IFormField, SurveyForm } from '../../../../../../../extViewmodels';
import { formatBirthdayString } from '../../../../../../../models/UiUtils';
import { useConditionalVisibility } from '../../../../shared/hooks/useConditionalVisibility';
import { clearHiddenFieldValues } from '../../../../shared/hooks/clearHiddenFieldValues';
import {
	useClientCreateCustomSurveyResponseDraftMutation,
	useClientCreateCustomSurveyResponseMutation,
	useClientGetCustomSurveyResponseByToken,
	useClientUpdateCustomSurveyResponseDraftMutation,
	useClientUpdateCustomSurveyResponseMutation,
} from '../../../../../../../queries';
import { useClientDeleteSurveyAttachmentByToken } from '../../../../../../../queries/Surveys/useClientDeleteSurveyAttachmentByToken';
import { Button } from '../../../../../../../web/components-v2/Button';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '../../../../../../../web/components-v2/Select';
import { Checkbox } from '../../../../../../../web/components-v2/Checkbox';
import { NumberedScaleInput } from '../../../../../../../web/components-v2/NumberedScaleInput';
import { TextArea } from '../../../../../../../web/components-v2/TextArea';
import { LoadingLogo } from '../../../../../../../web/components/LoadingLogo';
import { TextInput } from '../../../../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../../../../web/styles/styles';
import {
	ICustomSurvey,
	ICustomSurveyResponse,
	IOperationResultNoValue,
	IPublicFileAttachment,
} from '../../../../../../models/Api';
import { FileUpload } from '../../../../shared/FileUpload';
import { SurveySections } from '../../../../shared/components/SurveySections';
import { buildCustomPropertiesSchema } from '../../../../../../utils/validation';
import { type ResolvedScaleQuestion, resolveScaleQuestion } from '../../../../../../utils/scaleQuestions';
import { isSurveySection } from '../../../../shared/types';
import type { SectionedSurveyForm } from '../../../../shared/types';
import { ContactInfoStep } from './ContactInfoStep';
import { ErrorMessage } from './ErrorMessage';
import { styleSheet } from './styles';
import type { IFormValues } from './types';

type Props = {
	survey: ICustomSurvey;
	onResponseSubmitted?(response: ICustomSurveyResponse): void;
	surveyResponse: ICustomSurveyResponse;
	setSurveyResponse: React.Dispatch<React.SetStateAction<ICustomSurveyResponse>>;
};

const getDefaultValues = (survey: ICustomSurvey): IFormValues => {
	const customProperties: Record<string, string | IPublicFileAttachment[]> = {};
	survey.customForm?.fields.forEach(field => {
		if (field.fieldType === FormFieldType.FileUpload) {
			customProperties[field.id] = [] as IPublicFileAttachment[];
		} else {
			customProperties[field.id] = '';
		}
	});
	return {
		email: '',
		firstName: '',
		lastName: '',
		phoneNumber: '',
		birthday: '',
		address1: '',
		address2: '',
		city: '',
		stateProvince: '',
		postalCode: '',
		country: '',
		customProperties,
	};
};

// Phone number regex. Accepts a US/NANP number (the masked default) OR a free-form international
// number entered via MaskedInput passthrough, which always begins with `+`. The BE's libphonenumber
// is the source of truth for international validity; the FE only sanity-checks `+` followed by enough
// phone characters so we don't re-introduce the US-only gate that would reject `+55…` etc.
const usPhoneRegExp = /^(?:((\+1)?[\s-]?)?\(?[1-9]\d\d\)?[\s-]?[1-9]\d\d[\s-]?\d\d\d\d|\+[\d\s().-]{6,})$/;

// The mask and the calendar picker both emit zero-padded MM/DD/YYYY, so that is the only
// canonical stored format. Strict parsing still rejects genuinely invalid dates.
const birthdayFormat = 'MM/DD/YYYY';

// Contact-field error key → DOM id of its focusable control (custom fields use their own id).
const contactFieldElementIds: Record<string, string> = {
	firstName: 'client-firstName',
	lastName: 'client-lastName',
	email: 'client-email',
	phoneNumber: 'client-phone',
	birthday: 'client-birthday',
	address1: 'client-address1',
	city: 'client-city',
	stateProvince: 'client-state',
	postalCode: 'client-postalCode',
	country: 'client-country',
};

export const CustomSurveyResponseForm = ({ survey, onResponseSubmitted, setSurveyResponse, surveyResponse }: Props) => {
	const { params: urlParams = {} } = useRouteMatch<{
		token?: string;
	}>();

	const { data: recordedResponse } = useClientGetCustomSurveyResponseByToken({
		accessToken: urlParams.token,
		retry: false,
	});

	const deleteAttachmentMutation = useClientDeleteSurveyAttachmentByToken({});

	const [deletedAttachmentIds, setDeletedAttachmentIds] = React.useState<string[]>([]);

	const [loading, setLoading] = React.useState<boolean>(true);

	const formRef = React.useRef<HTMLFormElement>(null);

	// Build validation schema based on survey configuration
	const validationSchema = React.useMemo(() => {
		const options = survey.options || {};
		return yup.object().shape({
			email:
				options.contactEmailOptions?.collect && options.contactEmailOptions?.require
					? yup.string().trim().email('Invalid email').required('Email is required')
					: yup.string(),
			firstName:
				options.contactNameOptions?.collect && options.contactNameOptions?.require
					? yup.string().required('First name is required')
					: yup.string(),
			lastName:
				options.contactNameOptions?.collect && options.contactNameOptions?.require
					? yup.string().required('Last name is required')
					: yup.string(),
			phoneNumber:
				options.contactPhoneNumberOptions?.collect && options.contactPhoneNumberOptions?.require
					? yup.string().trim().matches(usPhoneRegExp, 'Must be a valid phone number').required('Phone is required')
					: yup
							.string()
							.optional()
							.trim()
							.matches(usPhoneRegExp, { excludeEmptyString: true, message: 'Must be a valid phone number' }),
			birthday:
				options.contactBirthdayOptions?.collect && options.contactBirthdayOptions?.require
					? yup
							.string()
							.required('Birthday is required')
							.test('valid-date', 'Please enter a valid date (MM/DD/YYYY)', value => {
								if (!value) return false;
								const date = moment(value, birthdayFormat, true);
								// Compare at day granularity so a birthday of today is accepted.
								return (
									date.isValid() &&
									date.isBefore(moment().add(1, 'day').startOf('day')) &&
									date.isAfter(moment().subtract(120, 'years'))
								);
							})
					: yup
							.string()
							.optional()
							.test('valid-date', 'Please enter a valid date (MM/DD/YYYY)', value => {
								if (!value) return true; // Optional field, empty is valid
								const date = moment(value, birthdayFormat, true);
								// Compare at day granularity so a birthday of today is accepted.
								return (
									date.isValid() &&
									date.isBefore(moment().add(1, 'day').startOf('day')) &&
									date.isAfter(moment().subtract(120, 'years'))
								);
							}),
			address1:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('Address line 1 is required')
					: yup.string().optional(),
			address2: yup.string().optional(),
			city:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('City is required')
					: yup.string().optional(),
			stateProvince:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('State/Province is required')
					: yup.string().optional(),
			postalCode:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('Postal Code is required')
					: yup.string().optional(),
			country:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('Country is required')
					: yup.string().optional(),
			customProperties: survey?.customForm?.fields
				? buildCustomPropertiesSchema(
						survey.customForm.fields.map(field => ({
							...field,
							id: field.id || '', // Ensure 'id' is defined, provide default if necessary
						})),
						survey.surveyForm
					)
				: yup.object().shape({}),
		});
	}, [survey]);

	const { handleSubmit, control, formState, getValues, setValue, trigger } = useForm<IFormValues>({
		defaultValues: getDefaultValues(survey),
		resolver: yupResolver(validationSchema),
	});

	// Watch customProperties for reactive conditional visibility evaluation
	// useWatch triggers re-renders when any nested customProperties value changes
	const watchedCustomProperties = useWatch({ control, name: 'customProperties' });
	const visibleFieldIds = useConditionalVisibility(survey.surveyForm, watchedCustomProperties);

	// Clear values of fields that become hidden
	const prevVisibleRef = React.useRef<Set<string> | undefined>(undefined);
	React.useEffect(() => {
		const prev = prevVisibleRef.current;
		if (prev && visibleFieldIds) {
			for (const fieldId of prev) {
				if (!visibleFieldIds.has(fieldId)) {
					const field = survey.customForm?.fields.find(f => f.id === fieldId);
					if (field) {
						const defaultValue: string | IPublicFileAttachment[] =
							field.fieldType === FormFieldType.FileUpload ? [] : '';
						setValue(`customProperties.${fieldId}`, defaultValue, { shouldValidate: true });
					}
				}
			}
		}
		prevVisibleRef.current = visibleFieldIds;
	}, [visibleFieldIds, survey.customForm?.fields, setValue]);

	// Handle mutation success
	const handleMutationSuccess = async (data: ICustomSurveyResponse) => {
		toast.success('Survey response successfully submitted');
		setSurveyResponse(data);

		if (deletedAttachmentIds.length) {
			for (const attachmentId of deletedAttachmentIds) {
				await deleteAttachmentMutation.mutateAsync({
					attachmentId,
					accessToken: urlParams.token,
					surveyType: 'custom',
				});
			}
		}
		setDeletedAttachmentIds([]);
		if (onResponseSubmitted) {
			onResponseSubmitted(data);
		}
	};
	const introMessage = React.useMemo(() => {
		const text = survey.options?.intro?.document;
		if (!text) {
			return null;
		}
		// remove lev-content tag
		const textWithoutLevContent = text.replace(/<lev-content[^>]*>/g, '').replace(/<\/lev-content>/g, '');
		// remove all p tag styles
		const textWithoutPStyles = textWithoutLevContent.replace(/<p[^>]*>/g, '<p>').replace(/<\/p>/g, '</p>');
		return <div className={css(styleSheet.introContent)} dangerouslySetInnerHTML={{ __html: textWithoutPStyles }} />;
	}, [survey]);

	// Handle mutation error
	const handleMutationError = (error: IOperationResultNoValue) => {
		// A 409 Conflict on submit means the draft was already finalized — the token was reused or a
		// concurrent submit won the race (see the draft-finalize flow in the API). Surface a clear,
		// distinct message instead of the raw server text (and distinct from the silent autosave 404).
		const message =
			error?.systemCode === 409
				? 'This survey response has already been submitted.'
				: error?.systemMessage || 'Error submitting survey response';
		toast.error(message);
		console.error('Mutation error:', error);
	};

	// Real mutations instead of mock ones
	const createResponseMutation = useClientCreateCustomSurveyResponseMutation({
		onSuccess: handleMutationSuccess,
		onError: handleMutationError,
	});

	const updateResponseMutation = useClientUpdateCustomSurveyResponseMutation({
		onSuccess: handleMutationSuccess,
		onError: handleMutationError,
	});

	/**
	 * Determines whether this survey has V2 sections (any surveyPart with _type 'SurveySection').
	 * Used to gate the partial two-step flow.
	 */
	const hasSections = (survey.surveyForm?.surveyParts ?? []).some(isSurveySection);

	/**
	 * True when the survey supports server-side draft partial submission AND has sections.
	 * The two-step flow (contact → questions) is only activated for V2+partials.
	 */
	const isPartial = hasSections && !!survey.allowPartialSubmission;

	/**
	 * Contact step: shown when partial mode is on and no draft has been created yet
	 * (surveyResponse has no id). Once a draft exists the form advances to the questions step.
	 */
	const isContactStep = isPartial && !surveyResponse?.id;

	// The contact-step "Continue" button is a type='button' (not a form submit), so it never sets
	// formState.isSubmitted. Track Continue attempts separately so contact-field validation errors
	// become visible after a failed Continue — otherwise the click silently does nothing.
	const [continueAttempted, setContinueAttempted] = React.useState(false);

	/**
	 * Draft mutation — creates the partial submission and returns a new Draft token.
	 * On success, both setSurveyResponse (updates local state) and onResponseSubmitted
	 * (swaps the URL token via the parent's h.replace) are called so subsequent calls
	 * use the draft token.
	 */
	const createDraftMutation = useClientCreateCustomSurveyResponseDraftMutation({
		onSuccess: (data?: ICustomSurveyResponse) => {
			if (!data) return;
			setSurveyResponse(data);
			if (onResponseSubmitted) {
				onResponseSubmitted(data);
			}
		},
		onError: handleMutationError,
	});

	// Once a draft autosave returns NotFound (404) the draft no longer exists (finalized by a
	// concurrent submit, or expired); stop autosaving silently rather than spamming errors.
	const draftGoneRef = React.useRef(false);
	const updateDraftMutation = useClientUpdateCustomSurveyResponseDraftMutation({
		onError: (error: IOperationResultNoValue) => {
			if (error?.systemCode === 404) {
				// Draft is gone (finalized by a concurrent submit, or expired) — stop autosaving
				// silently rather than spamming errors; the respondent can still complete and submit.
				draftGoneRef.current = true;
				return;
			}
			// Any other failure (network drop, 5xx) means the latest answers did NOT persist. Surface it
			// via the same toast pattern as handleMutationError so the respondent doesn't falsely believe
			// progress is saved. Autosave fires on every blur/change, so a stable toast id de-dupes — a
			// sustained outage shows one message, not one per keystroke.
			toast.error('We couldn’t save your latest answers. Check your connection and keep going.', {
				id: 'survey-autosave-error',
			});
		},
	});

	/**
	 * Autosave the in-progress answers onto the draft. Active only on the questions step of a
	 * partial survey (id + isDraft), so it can never fire before the draft exists or before the
	 * draft-token swap. No-ops once the draft is gone (404) so a finalized/expired draft doesn't
	 * keep retrying.
	 */
	const autosaveDraft = () => {
		if (!isPartial || !surveyResponse?.id || !surveyResponse?.isDraft || draftGoneRef.current) {
			return;
		}
		// Defense-in-depth, identical to handleSave: strip hidden-field values before persisting so the
		// draft never stores a stale answer for a field the respondent later hid. clearHiddenFieldValues
		// recomputes visibility from the values themselves, so it also closes the timing gap where this
		// runs from an onChange handler before the post-render clear-on-hide effect fires. Keeping the
		// draft clean makes cross-session resume hydrate correct values without patching the hydration sites.
		const safeCustomProperties = clearHiddenFieldValues(
			survey.surveyForm as SurveyForm | undefined,
			getValues('customProperties'),
			{
				clearedValue: fieldId =>
					survey.customForm?.fields?.find(f => f.id === fieldId)?.fieldType === FormFieldType.FileUpload ? [] : '',
			}
		);
		updateDraftMutation.mutate({
			accessToken: urlParams.token ?? '',
			body: { customProperties: safeCustomProperties } as ICustomSurveyResponse,
		});
	};

	/**
	 * Handles the "Continue" button on the contact step.
	 * Validates only the contact fields that the current survey collects, then
	 * POSTs to create a draft with the contact info.
	 */
	const handleContinue = async () => {
		// Reveal contact-field validation errors from here on, even though this isn't a form submit.
		setContinueAttempted(true);

		// Collect only the contact field names that are actually rendered so RHF
		// validates only those — not the question fields shown on the next step.
		const contactFields: Array<keyof IFormValues> = [];
		const opts = survey.options ?? {};
		if (opts.contactNameOptions?.collect) {
			contactFields.push('firstName', 'lastName');
		}
		if (opts.contactEmailOptions?.collect) {
			contactFields.push('email');
		}
		if (opts.contactPhoneNumberOptions?.collect) {
			contactFields.push('phoneNumber');
		}
		if (opts.contactBirthdayOptions?.collect) {
			contactFields.push('birthday');
		}
		if (opts.contactAddressOptions?.collect) {
			contactFields.push('address1', 'address2', 'city', 'stateProvince', 'postalCode', 'country');
		}

		const valid = await trigger(contactFields);
		if (!valid) {
			return;
		}

		const values = getValues();

		// Build contactInfo mirroring the exact shape used by handleSave for the full submit.
		createDraftMutation.mutate({
			token: urlParams.token ?? '',
			body: {
				contactInfo: {
					email: values.email?.trim(),
					firstName: values.firstName?.trim(),
					lastName: values.lastName?.trim(),
					phoneNumber: values.phoneNumber?.trim(),
					birthday: values.birthday?.trim(),
					address: {
						address1: values.address1?.trim(),
						address2: values.address2?.trim(),
						city: values.city?.trim(),
						stateProvince: values.stateProvince?.trim(),
						postalCode: values.postalCode?.trim(),
						country: values.country?.trim(),
					},
				},
			},
		});
	};

	// Load existing response data if available
	const isMountedRef = React.useRef(true);
	// Only show the full-screen loading indicator on the FIRST initialization. Subsequent re-runs of
	// this effect — post-submit `setSurveyResponse`, the token-swap `recordedResponse` refetch, and
	// resume seeding — must re-hydrate values WITHOUT flipping `loading` back on. That re-flash of the
	// spinner is what causes the flicker on submit.
	const hasInitializedRef = React.useRef(false);
	// Tracks the response identity we last hydrated form values from. The effect below re-runs on every
	// surveyResponse/recordedResponse change (post-Continue setSurveyResponse, the token-swap
	// recordedResponse refetch, resume seeding). Re-looping setValue over an already-hydrated response
	// would clobber answers the respondent has started typing on the questions step, so we hydrate the
	// customProperties of a given response id only once.
	const lastHydratedResponseIdRef = React.useRef<string | undefined>(undefined);

	React.useEffect(() => {
		isMountedRef.current = true;
		if (!hasInitializedRef.current) {
			setLoading(true);
		}

		const initializeForm = () => {
			// Check if component is still mounted before updating state
			if (!isMountedRef.current) return;

			// First try using the directly provided surveyResponse prop
			if (surveyResponse?.id) {
				// Populate form with existing response data. Contact fields are intentionally re-set on every
				// run (unlike customProperties below, which are gated): they're only editable on the contact
				// step, which unmounts once a draft exists, so re-setting them to the same persisted values
				// can't clobber in-progress input on the questions step.
				setValue('email', surveyResponse.contactInfo?.email || '');
				setValue('firstName', surveyResponse.contactInfo?.firstName || '');
				setValue('lastName', surveyResponse.contactInfo?.lastName || '');
				setValue('phoneNumber', surveyResponse.contactInfo?.phoneNumber || '');
				setValue('birthday', formatBirthdayString(surveyResponse.contactInfo?.birthday || ''));
				setValue('address1', surveyResponse.contactInfo?.address?.address1 || '');
				setValue('address2', surveyResponse.contactInfo?.address?.address2 || '');
				setValue('city', surveyResponse.contactInfo?.address?.city || '');
				setValue('stateProvince', surveyResponse.contactInfo?.address?.stateProvince || '');
				setValue('postalCode', surveyResponse.contactInfo?.address?.postalCode || '');
				setValue('country', surveyResponse.contactInfo?.address?.country || '');

				// Set custom properties — only once per response identity. Re-running this loop on a
				// response we've already hydrated would overwrite in-progress answers (see ref note above).
				if (lastHydratedResponseIdRef.current !== surveyResponse.id) {
					lastHydratedResponseIdRef.current = surveyResponse.id;
					const customProperties = surveyResponse?.customProperties || {};
					Object.entries(customProperties).forEach(([key, value]) => {
						setValue(`customProperties.${key}`, value || '');
					});
				}
			}
			// Then try the fetched recordedResponse if available
			else if (recordedResponse) {
				// Populate form with existing response data
				setValue('email', recordedResponse.contactInfo?.email || '');
				setValue('firstName', recordedResponse.contactInfo?.firstName || '');
				setValue('lastName', recordedResponse.contactInfo?.lastName || '');
				setValue('phoneNumber', recordedResponse.contactInfo?.phoneNumber || '');
				setValue('birthday', formatBirthdayString(recordedResponse.contactInfo?.birthday || ''));
				setValue('address1', recordedResponse.contactInfo?.address?.address1 || '');
				setValue('address2', recordedResponse.contactInfo?.address?.address2 || '');
				setValue('city', recordedResponse.contactInfo?.address?.city || '');
				setValue('stateProvince', recordedResponse.contactInfo?.address?.stateProvince || '');
				setValue('postalCode', recordedResponse.contactInfo?.address?.postalCode || '');
				setValue('country', recordedResponse.contactInfo?.address?.country || '');

				// Set custom properties — only once per response identity (see note in the branch above).
				if (recordedResponse.id && lastHydratedResponseIdRef.current !== recordedResponse.id) {
					lastHydratedResponseIdRef.current = recordedResponse.id;
					const customProperties = recordedResponse?.customProperties || {};
					Object.entries(customProperties).forEach(([key, value]) => {
						setValue(`customProperties.${key}`, value || '');
					});
				}

				// Seed surveyResponse state from the token-fetched response. This drives two
				// cross-session-resume behaviors:
				//  - Finalized response → the parent shows the confirmation page (gated on
				//    surveyResponse?.id && !surveyResponse?.isDraft). This is master's behavior.
				//  - Draft response → the two-step partial gate resolves to the questions step.
				// The !surveyResponse?.id guard keeps it loop-safe: once seeded, the effect
				// re-runs with surveyResponse?.id truthy and takes the first branch instead,
				// so setSurveyResponse is not called again.
				if (!surveyResponse?.id) {
					setSurveyResponse(recordedResponse);
				}
			}

			hasInitializedRef.current = true;
			setLoading(false);
		};

		// Short timeout to ensure form is ready for values
		const timeoutId = setTimeout(initializeForm, 300);

		return () => {
			isMountedRef.current = false;
			clearTimeout(timeoutId);
		};
	}, [recordedResponse, surveyResponse, setValue, setSurveyResponse]);

	// Use focus() (not scrollIntoView) to bring the first invalid field into view: inside a cross-origin
	// embed the parent page owns the scroll. JSDOM lacks scrollIntoView, hence the guards.
	const scrollToFirstError = (candidateElementIds: string[]) => {
		if (typeof document === 'undefined') {
			return;
		}
		// A comma-joined selector resolves to the first match in document (top-to-bottom) order,
		// and skips ids with no element (e.g. MultipleOptions, whose options use `${id}-${optId}`).
		const selector = candidateElementIds.map(id => `#${CSS.escape(id)}`).join(',');
		const target = selector ? document.querySelector<HTMLElement>(selector) : null;
		if (target) {
			if (typeof target.scrollIntoView === 'function') {
				target.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
			if (typeof target.focus === 'function') {
				target.focus();
			}
		} else if (formRef.current && typeof formRef.current.scrollIntoView === 'function') {
			formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	// react-hook-form validates before handleSave, so a failed submit lands here, not in handleSave.
	const onInvalid: SubmitErrorHandler<IFormValues> = errors => {
		const candidateElementIds = Object.keys(errors).flatMap(key => {
			if (key === 'customProperties' && errors.customProperties) {
				return Object.keys(errors.customProperties);
			}
			return contactFieldElementIds[key] ? [contactFieldElementIds[key]] : [];
		});
		toast.error(`Please fill out all required fields`);
		scrollToFirstError(candidateElementIds);
	};

	const handleSave = async () => {
		const values = getValues();

		const fileFields = survey.customForm?.fields?.filter(f => f.fieldType === FormFieldType.FileUpload);

		// Defense-in-depth: strip hidden-field values from the payload regardless of whether
		// the clear-on-hide UI effect already fired. This closes any timing gap between the
		// effect and submission, and handles cascading visibleWhen chains.
		// FileUpload fields clear to [] (not '') so the server receives an empty array.
		const safeCustomProperties = clearHiddenFieldValues(
			survey.surveyForm as SurveyForm | undefined,
			values.customProperties,
			{
				clearedValue: fieldId =>
					survey.customForm?.fields?.find(f => f.id === fieldId)?.fieldType === FormFieldType.FileUpload ? [] : '',
			}
		);

		const fileCustomProperties: Record<string, { fileName: string; attachmentId: string }[]> = {};
		Object.entries(safeCustomProperties).forEach(([key, value]) => {
			if (fileFields?.some(f => f.id === key)) {
				fileCustomProperties[key] = (value as IPublicFileAttachment[]).map(f => ({
					fileName: f.fileName,
					attachmentId: f.attachmentId,
				}));
			}
		});

		const body: ICustomSurveyResponse = {
			contactInfo: {
				email: values.email?.trim(),
				firstName: values.firstName?.trim(),
				lastName: values.lastName?.trim(),
				phoneNumber: values.phoneNumber?.trim(),
				birthday: values.birthday?.trim(),
				address: {
					address1: values.address1?.trim(),
					address2: values.address2?.trim(),
					city: values.city?.trim(),
					stateProvince: values.stateProvince?.trim(),
					postalCode: values.postalCode?.trim(),
					country: values.country?.trim(),
				},
			},
			customProperties: { ...safeCustomProperties, ...fileCustomProperties } as Record<
				string,
				string | IPublicFileAttachment[]
			>,
		};

		if (Object.values(values.customProperties).length) {
			const fieldsWithMissingInput = survey.customForm?.fields?.filter(
				x =>
					!x.isOptional &&
					!x.isHidden &&
					(visibleFieldIds === undefined || visibleFieldIds.has(x.id)) &&
					!values.customProperties[x.id]
			);
			if (fieldsWithMissingInput?.length) {
				toast.error(`Please fill out all required fields`);
				scrollToFirstError(fieldsWithMissingInput.map(field => field.id));
				return;
			}
		}

		// PUT/Update only for an ALREADY-FINALIZED response (a ChangeResponse edit). A still-draft
		// response is finalized via the POST/create path below: the BE detects the Draft-intent token
		// and converts the pinned draft in place (no new row), per the draft-submit flow.
		if (surveyResponse?.id && !surveyResponse?.isDraft) {
			const payload = {
				...surveyResponse,
				...body,
			};
			updateResponseMutation.mutate({
				accessToken: urlParams.token,
				body: payload,
			});
			return;
		}

		// POST/Create — a brand-new response, or finalizing a draft (Draft-intent token).
		createResponseMutation.mutate({ token: urlParams.token, body });
	};

	// Scale config per fieldId, resolved once per survey. Above the loading early-return to keep hook order stable.
	const scaleByFieldId = React.useMemo(() => {
		const map = new Map<string, ResolvedScaleQuestion>();
		for (const field of survey?.customForm?.fields ?? []) {
			const resolved = field.id ? resolveScaleQuestion(survey?.surveyForm as SurveyForm | undefined, field.id) : null;
			if (field.id && resolved) map.set(field.id, resolved);
		}
		return map;
	}, [survey?.customForm?.fields, survey?.surveyForm]);

	if (loading || !survey) {
		return (
			<div className={css(styleSheet.loadingLogo)}>
				<LoadingLogo size={200} />
			</div>
		);
	}

	/**
	 * Renders a single custom-form field (label + controller).
	 * Passed as the renderField prop to <SurveySections>, which handles
	 * section grouping and visibility filtering before calling this.
	 */
	const renderField = (f: IFormField<string>) => {
		const fieldDropdownOptions = f.options?.map(o => ({
			id: o.id ?? '',
			title: o.label ?? '',
		}));

		// A bare Integer field with no matching scale surveyPart isn't a scale question.
		const scale = scaleByFieldId.get(f.id) ?? null;
		return (
			<div key={f.id} className={css(styleSheet.formControlFullWidth)}>
				{/* `${f.id}-label` names the scale button group via aria-labelledby. */}
				<label id={`${f.id}-label`} htmlFor={f.id} className={css(styleSheet.label)}>
					{f.label} {!f.isOptional ? <span className={css(baseStyleSheet.required)}>*</span> : null}
				</label>
				<Controller
					name={`customProperties.${f.id}`}
					control={control}
					render={({ fieldState: { error }, field: { ref, ...fieldProps } }) => {
						if (f.fieldType === FormFieldType.String) {
							return (
								<>
									<TextInput
										type='text'
										inputId={f.id}
										inputClassName={css(styleSheet.textInput)}
										{...fieldProps}
										value={(fieldProps.value as string) || ''}
										onBlur={() => {
											fieldProps.onBlur?.();
											autosaveDraft();
										}}
									/>
									{formState.isSubmitted && formState?.errors.customProperties?.[f.id] && (
										<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
									)}
								</>
							);
						}

						if (f.fieldType === FormFieldType.Text) {
							return (
								<>
									<TextArea
										id={f.id}
										styles={[styleSheet.textArea]}
										{...fieldProps}
										value={(fieldProps.value as string) || ''}
										onBlur={() => {
											fieldProps.onBlur?.();
											autosaveDraft();
										}}
									/>
									{formState.isSubmitted && formState?.errors.customProperties?.[f.id] && (
										<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
									)}
								</>
							);
						}

						if (f.fieldType === FormFieldType.FileUpload) {
							return (
								<FileUpload
									token={urlParams.token}
									surveyType='custom'
									onFileSelected={files => {
										setValue(`customProperties.${f.id}`, files, { shouldValidate: true });
									}}
									files={(fieldProps.value as IPublicFileAttachment[]) ?? []}
									onDeleteFile={
										surveyResponse?.id
											? (file: IPublicFileAttachment) => {
													setDeletedAttachmentIds(prev => [...prev, file.attachmentId]);
												}
											: undefined
									}
									error={error}
								/>
							);
						}

						if (f.fieldType === FormFieldType.Option) {
							return (
								<>
									<Select
										// items lets SelectValue render the option label; without it base-ui shows the raw value (the id)
										items={fieldDropdownOptions?.map(opt => ({ value: opt.id, label: opt.title }))}
										value={(fieldProps.value as string) || undefined}
										onValueChange={(val: string) => {
											setValue(`customProperties.${f.id}`, val, { shouldValidate: true });
											autosaveDraft();
										}}
									>
										<SelectTrigger id={f.id} styles={[styleSheet.selectTrigger]}>
											<SelectValue placeholder='Select option' />
										</SelectTrigger>
										<SelectContent>
											{fieldDropdownOptions?.map(opt => (
												<SelectItem key={opt.id} value={opt.id}>
													{opt.title}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									{formState.isSubmitted && formState?.errors.customProperties?.[f.id] && (
										<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
									)}
								</>
							);
						}

						if (f.fieldType === FormFieldType.MultipleOptions) {
							const selectedOptions: string[] = (fieldProps?.value as string)?.split(',').filter(Boolean) ?? [];
							return (
								<>
									{f.options?.map(o => (
										<div key={o.id ?? ''} className={css(styleSheet.checkboxRow)}>
											<Checkbox
												id={`${f.id}-${o.id ?? ''}`}
												checked={selectedOptions.includes(o.id ?? '')}
												onCheckedChange={(checked: boolean | 'indeterminate') => {
													const optId = o.id ?? '';
													const next =
														checked === true ? [...selectedOptions, optId] : selectedOptions.filter(v => v !== optId);
													setValue(`customProperties.${f.id}`, next.join(','), { shouldValidate: true });
													autosaveDraft();
												}}
											/>
											<label htmlFor={`${f.id}-${o.id ?? ''}`} className={css(styleSheet.checkboxLabel)}>
												{o.label ?? ''}
											</label>
										</div>
									))}
									{formState.isSubmitted && formState?.errors.customProperties?.[f.id] && (
										<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
									)}
								</>
							);
						}

						// LinearScale / NPS — `scale` carries the bounds, labels, and which of the two it is.
						if (scale) {
							return (
								<>
									<NumberedScaleInput
										id={f.id}
										ariaLabelledby={`${f.id}-label`}
										min={scale.min}
										max={scale.max}
										value={(fieldProps.value as string) || undefined}
										lowLabel={scale.startLabel}
										highLabel={scale.endLabel}
										onValueChange={(val: string) => {
											setValue(`customProperties.${f.id}`, val, { shouldValidate: true });
											autosaveDraft();
										}}
									/>
									{formState.isSubmitted && formState?.errors.customProperties?.[f.id] && (
										<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
									)}
								</>
							);
						}

						return null;
					}}
				/>
			</div>
		);
	};

	// Single render path. The only structural differences across the V1 single-page form
	// and the V2 partial two-step flow are gated by `isContactStep`:
	//  - Contact step (V2 partial, no draft yet): hide SurveySections; show a "Continue"
	//    button that creates the draft instead of submitting.
	//  - Everything else (V2 questions step / V1 / non-partial): show SurveySections + the
	//    existing Submit button (with its dynamic label).
	return (
		<form ref={formRef} className={css(styleSheet.surveyForm)} onSubmit={handleSubmit(handleSave, onInvalid)}>
			{introMessage ?? null}
			<div className={css(styleSheet.formContainer)}>
				<ContactInfoStep
					survey={survey}
					control={control}
					formState={formState}
					showErrors={formState.isSubmitted || continueAttempted}
				/>
				{!isContactStep && (
					<SurveySections
						surveyForm={survey.surveyForm as SectionedSurveyForm | undefined}
						fields={survey.customForm?.fields ?? []}
						visibleFieldIds={visibleFieldIds}
						renderField={renderField}
					/>
				)}
			</div>

			<footer className={css(styleSheet.actions)}>
				<div className={css(styleSheet.divider)} />
				<div className={css(styleSheet.buttonContainer)}>
					{isContactStep ? (
						<Button
							type='button'
							variant='primary'
							onClick={handleContinue}
							disabled={createDraftMutation.isPending}
							isLoading={createDraftMutation.isPending}
						>
							Continue
						</Button>
					) : (
						<Button
							type='submit'
							variant='primary'
							disabled={createResponseMutation.isPending || updateResponseMutation.isPending}
							isLoading={createResponseMutation.isPending || updateResponseMutation.isPending}
						>
							{!surveyResponse?.id || surveyResponse?.isDraft ? 'Submit' : 'Update Response'}
						</Button>
					)}
				</div>
			</footer>
		</form>
	);
};
